import type { DateFragment, DateTime, Utc } from "@tento-chrono";
import type { Option } from "@tento-lona/sheets";

/**
 * Which span layers of a calendar days strip an existing-item drag ghost
 * touches. DOM-free (layers are opaque keys), so the package's own harness
 * tests it without an element.
 *
 * A layer's painted drag projection (`CalendarDaysStrip.#projectGhost`)
 * depends on the ghost in exactly two ways: the layer fades its own copy of
 * the dragged event (`isDragSource`), and the one host layer floats the
 * ghost at the dragged range. A layer that does neither paints its spans
 * unchanged, so a pointer move repaints only the layers whose projection
 * differs between the previous and the next ghost: at most the old and the
 * new host, plus the source layers when the drag starts.
 */

/** What the ghost projection reads from one span layer. */
export type GhostLayer = {
  readonly spans: readonly { readonly id: string }[];
  readonly windowed: DateFragment.Windowed;
};

/** What it reads from the in-flight ghost. */
export type GhostTarget = {
  readonly id: string;
  readonly range: DateTime.Range<Utc>;
};

/**
 * The layer that floats `ghost`. The dragged event lives in exactly one
 * layer, so only one paints the moving ghost (else every layer whose window
 * contains the range paints its own, the multi-lane "double ghost").
 * Same-day: the layer holding the source span while its window still
 * contains the range. Cross-day: the first layer whose window contains the
 * range (the source keeps only its faint placeholder); failing that, the
 * source layer. Only `isLive` layers count.
 */
export function ghostHostLayer<L>(
  layers: Iterable<readonly [L, GhostLayer]>,
  ghost: GhostTarget,
  isLive: (layer: L) => boolean,
): Option<L> {
  let sourceLayer: Option<L> = null;
  let sourceContainsRange = false;
  let targetLayer: Option<L> = null;
  for (const [layer, ctx] of layers) {
    if (!isLive(layer)) continue;
    const containsRange = ctx.windowed.truncate(ghost.range) != null;
    if (sourceLayer == null && ctx.spans.some((s) => s.id === ghost.id)) {
      sourceLayer = layer;
      sourceContainsRange = containsRange;
    }
    if (targetLayer == null && containsRange) {
      targetLayer = layer;
    }
  }
  return sourceContainsRange ? sourceLayer : (targetLayer ?? sourceLayer);
}

/**
 * The live layers whose drag projection differs between `previous` and
 * `next` (either may be null: no ghost), in `layers` order.
 */
export function layersToRepaintForGhost<L>(
  layers: ReadonlyMap<L, GhostLayer>,
  previous: Option<GhostTarget>,
  next: Option<GhostTarget>,
  isLive: (layer: L) => boolean,
): L[] {
  const previousHost = previous == null ? null : ghostHostLayer(layers, previous, isLive);
  const nextHost = next == null ? null : ghostHostLayer(layers, next, isLive);
  const repaint: L[] = [];
  for (const [layer, ctx] of layers) {
    if (!isLive(layer)) continue;
    if (
      projectionKey(ctx, previous, layer === previousHost) !==
        projectionKey(ctx, next, layer === nextHost)
    ) repaint.push(layer);
  }
  return repaint;
}

/** What `ghost` changes in one layer's paint; `""` when it changes nothing. */
function projectionKey(ctx: GhostLayer, ghost: Option<GhostTarget>, hosts: boolean): string {
  if (ghost == null) return "";
  const fades = ctx.spans.some((s) => s.id === ghost.id) ? ghost.id : "";
  const floats = hosts && ctx.windowed.truncate(ghost.range) != null
    ? `${ghost.id}@${ghost.range.start.mse}-${ghost.range.end.mse}`
    : "";
  return fades === "" && floats === "" ? "" : `${fades}|${floats}`;
}
